<template>
  <div>
    <!-- Dialog para visualizar logs -->
    <v-dialog v-model="internalDialog" max-width="1200px" scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">Logs de Cronograma</span>
          <v-spacer></v-spacer>
          <span class="subtitle-1">{{ selectedHost ? selectedHost.name : '' }}</span>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text style="height: 600px;">
          <div v-if="loadingLogs" class="text-center">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <p class="mt-2">Carregando logs...</p>
          </div>
          
          <div v-else-if="hostLogs && hostLogs.sistemas && hostLogs.sistemas.length > 0">
            <v-expansion-panels multiple>
              <v-expansion-panel v-for="sistema in hostLogs.sistemas" :key="sistema.sistema">
                <v-expansion-panel-header>
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" :color="sistema.tipo === 'manual' ? 'warning' : 'primary'">
                      {{ sistema.tipo === 'manual' ? 'mdi-hand-pointing-up' : 'mdi-cog' }}
                    </v-icon>
                    <span class="font-weight-bold">{{ sistema.sistema }}</span>
                    <v-spacer></v-spacer>
                    <v-chip small :color="sistema.tipo === 'manual' ? 'warning' : 'info'" text-color="white">
                      {{ sistema.tipo === 'manual' ? 'Manual' : 'Cronograma' }}
                    </v-chip>
                  </div>
                </v-expansion-panel-header>
                
                <v-expansion-panel-content>
                  <!-- Para AttManual (estrutura hierárquica) -->
                  <template v-if="sistema.tipo === 'manual' && sistema.subSistemas">
                    <v-expansion-panels class="mt-2">
                      <v-expansion-panel v-for="subSistema in sistema.subSistemas" :key="subSistema.sistema">
                        <v-expansion-panel-header>
                          <div class="d-flex align-center">
                            <v-icon class="mr-2" color="success">mdi-desktop-classic</v-icon>
                            <span class="font-weight-medium">{{ subSistema.sistema }}</span>
                            <v-spacer></v-spacer>
                            <v-chip small color="success" text-color="white">
                              {{ subSistema.datas.length }} execução(ões)
                            </v-chip>
                          </div>
                        </v-expansion-panel-header>
                        
                        <v-expansion-panel-content>
                          <v-expansion-panels class="mt-2">
                            <v-expansion-panel v-for="data in subSistema.datas" :key="data.data">
                              <v-expansion-panel-header>
                                <div class="d-flex align-center">
                                  <v-icon class="mr-2" color="info">mdi-calendar</v-icon>
                                  <span>{{ data.data }}</span>
                                  <v-spacer></v-spacer>
                                  <v-chip small color="info" text-color="white">
                                    {{ data.arquivos.length }} arquivo(s)
                                  </v-chip>
                                </div>
                              </v-expansion-panel-header>
                              
                              <v-expansion-panel-content>
                                <v-list dense>
                                  <v-list-item 
                                    v-for="arquivo in data.arquivos" 
                                    :key="arquivo.nome"
                                    @click="viewLogFile(`AttManual/${subSistema.sistema}`, data.data, arquivo)"
                                    class="cursor-pointer"
                                  >
                                    <v-list-item-icon>
                                      <v-icon v-if="arquivo.nome.endsWith('.png')" color="orange">mdi-image</v-icon>
                                      <v-icon v-else-if="arquivo.nome.endsWith('.mp4')" color="red">mdi-video</v-icon>
                                      <v-icon v-else color="blue">mdi-file-document</v-icon>
                                    </v-list-item-icon>
                                    
                                    <v-list-item-content>
                                      <v-list-item-title>{{ arquivo.nome }}</v-list-item-title>
                                      <v-list-item-subtitle>
                                        Tamanho: {{ formatFileSize(arquivo.tamanho) }} | 
                                        Modificado: {{ formatDate(arquivo.modificado) }}
                                      </v-list-item-subtitle>
                                    </v-list-item-content>
                                    
                                    <v-list-item-action>
                                      <v-btn icon small @click.stop="viewLogFile(`AttManual/${subSistema.sistema}`, data.data, arquivo)">
                                        <v-icon>mdi-eye</v-icon>
                                      </v-btn>
                                    </v-list-item-action>
                                  </v-list-item>
                                </v-list>
                              </v-expansion-panel-content>
                            </v-expansion-panel>
                          </v-expansion-panels>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </template>
                  
                  <!-- Para sistemas normais (cronograma) -->
                  <template v-else>
                    <v-expansion-panels class="mt-2">
                      <v-expansion-panel v-for="data in sistema.datas" :key="data.data">
                        <v-expansion-panel-header>
                          <div class="d-flex align-center">
                            <v-icon class="mr-2" color="success">mdi-calendar</v-icon>
                            <span>{{ data.data }}</span>
                            <v-spacer></v-spacer>
                            <v-chip small color="success" text-color="white">
                              {{ data.arquivos.length }} arquivo(s)
                            </v-chip>
                          </div>
                        </v-expansion-panel-header>
                        
                        <v-expansion-panel-content>
                          <v-list dense>
                            <v-list-item 
                              v-for="arquivo in data.arquivos" 
                              :key="arquivo.nome"
                              @click="viewLogFile(sistema.sistema, data.data, arquivo)"
                              class="cursor-pointer"
                            >
                              <v-list-item-icon>
                                <v-icon v-if="arquivo.nome.endsWith('.png')" color="orange">mdi-image</v-icon>
                                <v-icon v-else-if="arquivo.nome.endsWith('.mp4')" color="red">mdi-video</v-icon>
                                <v-icon v-else color="blue">mdi-file-document</v-icon>
                              </v-list-item-icon>
                              
                              <v-list-item-content>
                                <v-list-item-title>{{ arquivo.nome }}</v-list-item-title>
                                <v-list-item-subtitle>
                                  Tamanho: {{ formatFileSize(arquivo.tamanho) }} | 
                                  Modificado: {{ formatDate(arquivo.modificado) }}
                                </v-list-item-subtitle>
                              </v-list-item-content>
                              
                              <v-list-item-action>
                                <v-btn icon small @click.stop="viewLogFile(sistema.sistema, data.data, arquivo)">
                                  <v-icon>mdi-eye</v-icon>
                                </v-btn>
                              </v-list-item-action>
                            </v-list-item>
                          </v-list>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </template>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
          
          <div v-else class="text-center">
            <v-icon size="64" color="grey">mdi-file-document-remove</v-icon>
            <p class="mt-2 grey--text">Nenhum log de cronograma encontrado para este host.</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog para visualizar conteúdo de arquivo de log -->
    <v-dialog v-model="logFileDialog" max-width="1200px" scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">{{ selectedLogFile ? selectedLogFile.nome : '' }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeLogFileDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text :style="selectedLogFile && selectedLogFile.isVideo ? 'height: 700px;' : 'height: 500px;'">
          <div v-if="loadingLogFile" class="text-center">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <p class="mt-2">Carregando arquivo...</p>
          </div>
          
          <div v-else-if="selectedLogFile">
            <!-- Exibir imagem -->
            <div v-if="selectedLogFile.isImage" class="text-center">
              <img 
                :src="selectedLogFile.url" 
                :alt="selectedLogFile.nome"
                style="max-width: 100%; height: auto;"
              />
            </div>
            
            <!-- Exibir vídeo -->
            <div v-else-if="selectedLogFile.isVideo" class="text-center">
              <video 
                :src="selectedLogFile.url" 
                controls
                style="max-width: 100%; height: auto;"
                preload="metadata"
                @error="onVideoError"
              >
                Seu navegador não suporta a reprodução de vídeos.
              </video>
            </div>
            
            <!-- Exibir conteúdo de texto -->
            <div v-else>
              <pre class="log-content">{{ selectedLogFile.conteudo }}</pre>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeLogFileDialog">
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import hostsService from '@/services/hostsService'
import authService from '@/services/authService'
import axios from 'axios'
import { getApiUrl, getDefaultHeaders } from '@/config/env'

export default {
  name: 'LogViewer',
  
  props: {
    value: {
      type: Boolean,
      default: false
    },
    selectedHost: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      // Logs
      loadingLogs: false,
      hostLogs: null,
      
      // Arquivo de log selecionado
      selectedLogFile: null,
      logFileDialog: false,
      loadingLogFile: false,
      
      // Flag para evitar duplicação de requisições
      isLoadingLogs: false
    }
  },

  computed: {
    internalDialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    }
  },

  watch: {
    value: {
      handler(newVal) {
        if (newVal && this.selectedHost && !this.isLoadingLogs) {
          this.loadLogs()
        }
        
        // Limpar URLs temporárias quando o diálogo for fechado
        if (!newVal) {
          this.cleanupTempUrl()
        }
      },
      immediate: true
    },
    
    selectedHost: {
      handler(newHost, oldHost) {
        // Só carrega logs se o host mudou e o dialog está aberto
        if (newHost && this.value && !this.isLoadingLogs && newHost !== oldHost) {
          this.loadLogs()
        }
      }
    },

    logFileDialog: {
      handler(newVal) {
        // Limpar URLs temporárias quando o diálogo de arquivo for fechado
        if (!newVal) {
          this.cleanupTempUrl()
        }
      }
    }
  },

  beforeDestroy() {
    // Limpar URLs temporárias quando o componente for destruído
    this.cleanupTempUrl()
  },

  methods: {
    async loadLogs() {
      if (!this.selectedHost || this.isLoadingLogs) return
      
      this.isLoadingLogs = true
      this.loadingLogs = true
      try {
        const hostId = this.selectedHost.hostId || this.selectedHost.id
        const response = await hostsService.getLogsByHostId(hostId)
        
        if (response && response.success) {
          this.hostLogs = response.logs
        } else {
          throw new Error(response.message || 'Erro ao buscar logs')
        }
      } catch (error) {
        console.error('Erro ao buscar logs:', error)
        this.$emit('show-snackbar', error.message || 'Erro ao buscar logs', 'error')
      } finally {
        this.loadingLogs = false
        this.isLoadingLogs = false
      }
    },

    async viewLogFile(sistema, data, arquivo) {
      // Verificar se o usuário está autenticado
      if (!authService.isAuthenticated()) {
        this.$emit('show-snackbar', 'Você não está autenticado. Faça login novamente.', 'error')
        this.$emit('redirect-to-login')
        return
      }
      
      this.loadingLogFile = true
      
      try {
        const hostId = this.selectedHost.hostId || this.selectedHost.id
        // Obter token da mesma forma que o interceptor do api.js
        const token = localStorage.getItem('authToken')
        
        if (!token) {
          this.$emit('show-snackbar', 'Token de autenticação não encontrado. Faça login novamente.', 'error')
          this.$emit('redirect-to-login')
          return
        }
        
        const isImage = arquivo.nome.endsWith('.png') || arquivo.nome.endsWith('.jpg') || arquivo.nome.endsWith('.jpeg')
        const isVideo = arquivo.nome.endsWith('.mp4')
        
        if (isVideo || isImage) {
          // Para vídeos e imagens, fazer requisição direta com axios e token Bearer no header
          // Encoding da URL para caracteres especiais
          const encodedSistema = encodeURIComponent(sistema)
          const encodedData = encodeURIComponent(data)
          const encodedFileName = encodeURIComponent(arquivo.nome)
          
          // URL sem token - token vai no header Authorization
          const fullUrl = getApiUrl(`/hosts/${hostId}/logs/${encodedSistema}/${encodedData}/${encodedFileName}`)
          
          const requestConfig = {
            headers: {
              ...getDefaultHeaders(),
              'Authorization': `Bearer ${token}`,
              'Accept': isVideo ? 'video/mp4' : 'image/*'
            },
            responseType: 'blob',
            timeout: 30000 // 30 segundos timeout
          }
          
          try {
            const response = await axios.get(fullUrl, requestConfig)
            
            // Criar URL temporária para o blob
            const mimeType = isVideo ? 'video/mp4' : this.getImageMimeType(arquivo.nome)
            const mediaBlob = new Blob([response.data], { type: mimeType })
            const mediaUrl = URL.createObjectURL(mediaBlob)
            
            this.selectedLogFile = {
              nome: arquivo.nome,
              url: mediaUrl,
              isImage: isImage,
              isVideo: isVideo,
              conteudo: null
            }
            
          } catch (requestError) {
            console.error('Erro na requisição de arquivo de mídia:', requestError)
            
            if (requestError.response?.status === 401) {
              // Verificar se o token ainda é válido
              const isTokenValid = authService.isAuthenticated()
              
              if (!isTokenValid) {
                this.$emit('show-snackbar', 'Sessão expirada. Faça login novamente.', 'error')
                this.$emit('redirect-to-login')
                return
              } else {
                // Token válido mas servidor rejeitou - problema de configuração
                this.$emit('show-snackbar', 'Erro de autorização ao acessar arquivo. Token presente mas rejeitado pelo servidor.', 'error')
                
                // Tentar fazer a requisição usando o serviço normal como fallback
                try {
                  const fallbackResponse = await hostsService.getLogFile(hostId, sistema, data, arquivo.nome)
                  if (fallbackResponse && fallbackResponse.success) {
                    this.selectedLogFile = {
                      nome: arquivo.nome,
                      url: fallbackResponse.url,
                      isImage: isImage,
                      isVideo: isVideo,
                      conteudo: fallbackResponse.conteudo
                    }
                  } else {
                    throw new Error('Fallback também falhou')
                  }
                } catch (fallbackError) {
                  console.error('Erro no fallback:', fallbackError)
                  throw requestError // Lançar o erro original
                }
              }
            } else {
              throw requestError
            }
          }
        } else {
          // Para outros tipos de arquivo, usar o serviço normal
          const response = await hostsService.getLogFile(hostId, sistema, data, arquivo.nome)
          
          if (response && response.success) {
            this.selectedLogFile = {
              nome: arquivo.nome,
              url: response.url,
              isImage: false,
              isVideo: false,
              conteudo: response.conteudo
            }
          } else {
            throw new Error(response.message || 'Erro ao visualizar arquivo')
          }
        }
      } catch (error) {
        console.error('Erro ao visualizar arquivo:', error)
        
        let errorMessage = 'Erro ao visualizar arquivo'
        if (error.response?.status === 401) {
          errorMessage = 'Não autorizado a acessar este arquivo. Verifique sua autenticação.'
        } else if (error.response?.status === 404) {
          errorMessage = 'Arquivo não encontrado no servidor.'
        } else if (error.message) {
          errorMessage = error.message
        }
        
        this.$emit('show-snackbar', errorMessage, 'error')
      } finally {
        this.loadingLogFile = false
      }
      this.logFileDialog = true
    },

    getImageMimeType(fileName) {
      const extension = fileName.toLowerCase().split('.').pop()
      switch (extension) {
        case 'png':
          return 'image/png'
        case 'jpg':
        case 'jpeg':
          return 'image/jpeg'
        default:
          return 'image/png'
      }
    },

    cleanupTempUrl() {
      if (this.selectedLogFile && (this.selectedLogFile.isVideo || this.selectedLogFile.isImage) && this.selectedLogFile.url) {
        URL.revokeObjectURL(this.selectedLogFile.url)
      }
    },

    closeLogFileDialog() {
      this.cleanupTempUrl()
      this.logFileDialog = false
      this.selectedLogFile = null
    },

    closeDialog() {
      this.$emit('input', false)
    },

    formatFileSize(size) {
      if (size < 1024) {
        return size + ' bytes'
      } else if (size < 1024 * 1024) {
        return (size / 1024).toFixed(2) + ' KB'
      } else if (size < 1024 * 1024 * 1024) {
        return (size / (1024 * 1024)).toFixed(2) + ' MB'
      } else {
        return (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB'
      }
    },

    formatDate(dateString) {
      if (!dateString) return ''
      try {
        return new Date(dateString).toLocaleString('pt-BR')
      } catch {
        return dateString
      }
    },

    onVideoError(event) {
      console.error('[VIDEO] error - Erro no vídeo:', event)
      console.error('[VIDEO] error details:', event.target.error)
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.log-content {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 400px;
  overflow-y: auto;
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  line-height: 1.4;
}
</style> 